import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useCreateCustomerMutation, useCustomerPagesInfiniteQuery, useCustomerQuery } from '@/api/endpoints';
import { apiFieldErrors } from '@/api/baseApi';
import { Input } from '@/components/ui';
import { useAccess } from '@/hooks/useAccess';
import { CreatableSelect } from '@/components/CreatableSelect';
import { pickerPages, usePickerSearch } from '@/lib/pickerPages';

const emptyCustomer = { name: '', phone: '', email: '' };

interface CustomerPickerProps {
  label?: string;
  value: string;
  onChange: (customerId: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  /** What the blank option means here — a sale has walk-ins, a filter has "all". */
  placeholder?: string;
}

/**
 * A customer dropdown that can create the customer it is missing.
 *
 * A new customer usually appears at the counter, half-way through ringing up
 * their first sale. Sending the cashier to the customers screen loses the
 * half-built sale, so the name, phone and email are taken here — the rest of
 * the record (credit limit, group, addresses) is filled in later, by whoever
 * decides those things.
 */
export function CustomerPicker({
  label = 'Customer',
  value,
  onChange,
  hint,
  error,
  required,
  placeholder = 'Walk-in',
}: CustomerPickerProps) {
  const { can } = useAccess();
  const [createCustomer, createState] = useCreateCustomerMutation();
  const [form, setForm] = useState(emptyCustomer);

  const fieldErrors = apiFieldErrors(createState.error);

  // Paged and searched on the server: a business with thousands of customers
  // must still be able to find the one at the counter.
  const [search, setSearch] = usePickerSearch();
  const { items: customers, picker } = pickerPages(
    useCustomerPagesInfiniteQuery({ isActive: true, search }),
    setSearch,
  );

  // The chosen customer may not be on a page loaded yet — an older sale being
  // edited, say — and still has to be shown by name.
  const loaded = !value || customers.some((customer) => customer.id === value);
  const { data: chosen } = useCustomerQuery(value, { skip: loaded });
  // While searching it stays out of the results; the Select keeps its name.
  const options = chosen && !loaded && !search ? [chosen, ...customers] : customers;

  return (
    <CreatableSelect
      label={label}
      noun="customer"
      required={required}
      value={value}
      onChange={onChange}
      hint={hint}
      error={error}
      placeholder={placeholder}
      picker={picker}
      options={options.map((customer) => ({
        id: customer.id,
        label: `${customer.name} (${customer.code})`,
      }))}
      canCreate={can(P.CUSTOMER_CREATE)}
      creating={createState.isLoading}
      createDisabled={!form.name}
      onReset={() => setForm(emptyCustomer)}
      onCreate={async () => {
        const created = await createCustomer({
          name: form.name,
          phone: form.phone || undefined,
          email: form.email || undefined,
        }).unwrap();
        return created.id;
      }}
      form={
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Name"
            placeholder="Kampala Retailers Ltd"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="Phone"
            placeholder="+256 700 000 000"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
            error={fieldErrors.phone}
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            error={fieldErrors.email}
            hint="A code is allocated automatically."
          />
        </div>
      }
    />
  );
}
